class SidebarSheetController {
  constructor(orchestrator) {
    this.orchestrator = orchestrator;

    this.dragging = false;
    this.dragStartY = 0;
    this.dragStartHeightPercent = orchestrator.SIDEBAR_SHEET_DEFAULT;
    this.dragFramePending = false;
    this.dragLatestY = 0;
  }

  isMobileSheetLayout() {
    return window.matchMedia(this.orchestrator.MOBILE_SHEET_MEDIA_QUERY)
      .matches;
  }

  applySidebarSheetHeight(percent, { animate = false } = {}) {
    const { SIDEBAR_SHEET_DEFAULT, SIDEBAR_SHEET_MAX, selectionState } =
      this.orchestrator;
    const { customizerSidebar } = this.orchestrator.dom;
    if (!customizerSidebar) return;

    const clamped = Math.min(
      SIDEBAR_SHEET_MAX,
      Math.max(SIDEBAR_SHEET_DEFAULT, percent),
    );
    selectionState.sidebarSheetHeightPercent = clamped;

    const solidRatio = Math.min(100, (SIDEBAR_SHEET_DEFAULT / clamped) * 100);

    customizerSidebar.classList.toggle('is-sheet-dragging', !animate);
    customizerSidebar.classList.toggle('is-sheet-animating', animate);
    customizerSidebar.style.setProperty(
      '--sidebar-sheet-height',
      `${clamped}%`,
    );
    customizerSidebar.style.setProperty(
      '--sidebar-solid-height',
      `${solidRatio}%`,
    );
  }

  setSidebarSheetExpanded(expanded, { animate = true } = {}) {
    const { SIDEBAR_SHEET_DEFAULT, SIDEBAR_SHEET_MAX, selectionState } =
      this.orchestrator;
    const { customizerSidebar } = this.orchestrator.dom;
    if (!customizerSidebar) return;

    selectionState.sidebarSheetExpanded = Boolean(expanded);
    this.applySidebarSheetHeight(
      selectionState.sidebarSheetExpanded
        ? SIDEBAR_SHEET_MAX
        : SIDEBAR_SHEET_DEFAULT,
      { animate },
    );
    customizerSidebar.classList.toggle(
      'is-sheet-expanded',
      selectionState.sidebarSheetExpanded,
    );
    customizerSidebar.classList.remove('is-sheet-dragging');
  }

  resetSidebarSheetHeight() {
    this.setSidebarSheetExpanded(false, { animate: false });
  }

  updateDragHeight() {
    const { modalContent } = this.orchestrator.dom;
    this.dragFramePending = false;
    if (!this.dragging) return;
    const contentHeight = modalContent.clientHeight || 1;
    const deltaY = this.dragStartY - this.dragLatestY;
    const deltaPercent = (deltaY / contentHeight) * 100;
    this.applySidebarSheetHeight(this.dragStartHeightPercent + deltaPercent, {
      animate: false,
    });
  }

  handleDragEnd(event) {
    const { SIDEBAR_SHEET_DEFAULT, SIDEBAR_SHEET_MAX } = this.orchestrator;
    const { sidebarSheetHandle, modalContent } = this.orchestrator.dom;

    if (!this.dragging) return;
    this.dragging = false;
    this.dragFramePending = false;
    sidebarSheetHandle.releasePointerCapture?.(event.pointerId);

    this.dragLatestY = event.clientY ?? this.dragLatestY;
    const contentHeight = modalContent.clientHeight || 1;
    const deltaPercent =
      ((this.dragStartY - this.dragLatestY) / contentHeight) * 100;
    const currentHeight = Math.min(
      SIDEBAR_SHEET_MAX,
      Math.max(
        SIDEBAR_SHEET_DEFAULT,
        this.dragStartHeightPercent + deltaPercent,
      ),
    );
    const midpoint = (SIDEBAR_SHEET_DEFAULT + SIDEBAR_SHEET_MAX) / 2;
    this.setSidebarSheetExpanded(currentHeight >= midpoint, { animate: true });
  }

  initialize() {
    const { selectionState, SIDEBAR_SHEET_DEFAULT, MOBILE_SHEET_MEDIA_QUERY } =
      this.orchestrator;
    const { sidebarSheetHandle, customizerSidebar, modalContent } =
      this.orchestrator.dom;

    if (!sidebarSheetHandle || !customizerSidebar || !modalContent) return;
    if (sidebarSheetHandle.dataset.ready === 'true') return;
    sidebarSheetHandle.dataset.ready = 'true';

    this.resetSidebarSheetHeight();

    sidebarSheetHandle.addEventListener('pointerdown', event => {
      if (!this.isMobileSheetLayout()) return;
      this.dragging = true;
      this.dragStartY = event.clientY;
      this.dragLatestY = event.clientY;
      this.dragStartHeightPercent = selectionState.sidebarSheetHeightPercent;
      customizerSidebar.classList.add('is-sheet-dragging');
      customizerSidebar.classList.remove('is-sheet-animating');
      sidebarSheetHandle.setPointerCapture?.(event.pointerId);
      event.preventDefault();
    });

    sidebarSheetHandle.addEventListener('pointermove', event => {
      if (!this.dragging) return;
      this.dragLatestY = event.clientY;
      if (this.dragFramePending) return;
      this.dragFramePending = true;
      requestAnimationFrame(() => this.updateDragHeight());
    });

    sidebarSheetHandle.addEventListener('pointerup', event =>
      this.handleDragEnd(event),
    );
    sidebarSheetHandle.addEventListener('pointercancel', event =>
      this.handleDragEnd(event),
    );

    window
      .matchMedia(MOBILE_SHEET_MEDIA_QUERY)
      .addEventListener('change', event => {
        if (!event.matches) {
          customizerSidebar.style.removeProperty('--sidebar-sheet-height');
          customizerSidebar.style.removeProperty('--sidebar-solid-height');
          customizerSidebar.classList.remove(
            'is-sheet-expanded',
            'is-sheet-animating',
            'is-sheet-dragging',
          );
          selectionState.sidebarSheetExpanded = false;
          selectionState.sidebarSheetHeightPercent = SIDEBAR_SHEET_DEFAULT;
        } else {
          this.resetSidebarSheetHeight();
        }
      });

    window.addEventListener('resize', () => {
      if (!this.isMobileSheetLayout()) return;
      this.setSidebarSheetExpanded(selectionState.sidebarSheetExpanded, {
        animate: false,
      });
    });
  }
}

window.SidebarSheetController = SidebarSheetController;
